/**
 * @file Shared Calendar notification threshold control helpers.
 */

import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    COMPACT_INTEGER_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    createSelectControl,
} from "../../shared/ui/controls/selectControl.js";

export const CALENDAR_THRESHOLD_ITEM_TYPES = Object.freeze({
    EVENT: "event",
    REMINDER: "reminder",
    LEAVE: "leave",
});

export const CALENDAR_THRESHOLD_UNITS = Object.freeze({
    MINUTES: "minutes",
    HOURS: "hours",
    DAYS: "days",
});

const MAX_THRESHOLD_DAYS = 365;
const MAX_THRESHOLD_HOURS = MAX_THRESHOLD_DAYS * 24;
const MAX_THRESHOLD_MINUTES = MAX_THRESHOLD_HOURS * 60;
const UNIT_LABELS = Object.freeze({
    [CALENDAR_THRESHOLD_UNITS.MINUTES]: "mins",
    [CALENDAR_THRESHOLD_UNITS.HOURS]: "hours",
    [CALENDAR_THRESHOLD_UNITS.DAYS]: "days",
});
const DEFAULT_UNIT_BY_TYPE = Object.freeze({
    [CALENDAR_THRESHOLD_ITEM_TYPES.EVENT]: CALENDAR_THRESHOLD_UNITS.DAYS,
    [CALENDAR_THRESHOLD_ITEM_TYPES.REMINDER]: CALENDAR_THRESHOLD_UNITS.DAYS,
    [CALENDAR_THRESHOLD_ITEM_TYPES.LEAVE]: CALENDAR_THRESHOLD_UNITS.DAYS,
});
const THRESHOLD_SOURCES = Object.freeze({
    ADMIN_DEFAULT: "admin_default",
    USER: "user",
});
const MAX_THRESHOLDS = 20;
const THRESHOLD_VALUE_INPUT_CLASSES = [
    COMPACT_INTEGER_CLASSES,
    "w-16 text-center",
].join(" ");
const thresholdUnitControls = new WeakMap();
const thresholdDefaultUnitControls = new WeakMap();
const thresholdDefaultChangeHandlers = new WeakMap();
let nextDraftThresholdId = 1;

/**
 * Escapes a value for an HTML attribute.
 * @param {*} value Raw value.
 * @returns {string} Escaped attribute value.
 */
function attrValue(value = "") {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function createDraftThresholdId() {
    const id = `draft-threshold-${Date.now().toString(36)}-${nextDraftThresholdId}`;
    nextDraftThresholdId += 1;
    return id;
}

function getUnitOptions(allowedUnits) {
    return allowedUnits.map((unit) => ({
        value: unit,
        label: UNIT_LABELS[unit] || unit,
    }));
}

function getUnitLabel(unit) {
    return UNIT_LABELS[unit] || unit;
}

function getUnitDisplayLabel(unit, value) {
    const normalizedValue = Number(value);
    if (unit === CALENDAR_THRESHOLD_UNITS.MINUTES) {
        return normalizedValue === 1 ? "minute" : "minutes";
    }
    if (unit === CALENDAR_THRESHOLD_UNITS.HOURS) {
        return normalizedValue === 1 ? "hour" : "hours";
    }
    if (unit === CALENDAR_THRESHOLD_UNITS.DAYS) {
        return normalizedValue === 1 ? "day" : "days";
    }
    return getUnitLabel(unit);
}

function isExistingItem(item) {
    return Boolean(item?.id || item?.sourceId);
}

/**
 * Returns the maximum supported threshold value for a unit.
 * @param {string} unit Threshold unit.
 * @returns {number} Maximum value.
 */
export function getCalendarThresholdMaxValue(unit) {
    if (unit === CALENDAR_THRESHOLD_UNITS.MINUTES) {
        return MAX_THRESHOLD_MINUTES;
    }
    if (unit === CALENDAR_THRESHOLD_UNITS.HOURS) {
        return MAX_THRESHOLD_HOURS;
    }
    return MAX_THRESHOLD_DAYS;
}

/**
 * Returns units allowed for a Calendar item type.
 * @param {string} itemType Calendar item type.
 * @param {object} [options={}] Unit options.
 * @param {boolean} [options.isAllDay=false] Whether the item is all-day.
 * @returns {Array<string>} Allowed units.
 */
export function getAllowedCalendarThresholdUnits(itemType, options = {}) {
    if (itemType === CALENDAR_THRESHOLD_ITEM_TYPES.LEAVE ||
        options.isAllDay === true) {
        return [CALENDAR_THRESHOLD_UNITS.DAYS];
    }
    return [
        CALENDAR_THRESHOLD_UNITS.MINUTES,
        CALENDAR_THRESHOLD_UNITS.HOURS,
        CALENDAR_THRESHOLD_UNITS.DAYS,
    ];
}

/**
 * Returns a stable admin default id for an item type.
 * @param {string} itemType Calendar item type.
 * @returns {string} Default threshold id.
 */
export function getCalendarThresholdDefaultId(itemType) {
    return `default-calendar-${String(itemType || "item")}`;
}

/**
 * Normalizes one Calendar threshold draft.
 * @param {*} threshold Raw threshold.
 * @param {object} params Normalization params.
 * @param {string} params.itemType Calendar item type.
 * @param {boolean} [params.isAllDay=false] Whether the item is all-day.
 * @returns {object|null} Normalized threshold or null.
 */
export function normalizeCalendarThresholdDraft(threshold, {
    itemType,
    isAllDay = false,
}) {
    if (!threshold || typeof threshold !== "object") {return null;}
    if (threshold.enabled === false) {return null;}

    const allowedUnits = getAllowedCalendarThresholdUnits(
        itemType,
        { isAllDay },
    );
    const fallbackUnit = DEFAULT_UNIT_BY_TYPE[itemType] ||
        CALENDAR_THRESHOLD_UNITS.DAYS;
    const unit = allowedUnits.includes(threshold.unit) ?
        threshold.unit :
        fallbackUnit;
    const value = Number(threshold.value);
    if (
        !Number.isInteger(value) ||
        value < 1 ||
        value > getCalendarThresholdMaxValue(unit)
    ) {
        return null;
    }

    return {
        id: String(threshold.id || getCalendarThresholdDefaultId(itemType)),
        unit,
        value,
        enabled: true,
    };
}

/**
 * Returns an admin default draft for rendering.
 * @param {*} threshold Raw threshold.
 * @param {object} params Draft params.
 * @param {string} params.itemType Calendar item type.
 * @param {string} [params.fallbackUnit] Fallback unit.
 * @returns {object} Draft threshold.
 */
export function getCalendarThresholdDefaultDraft(threshold, {
    itemType,
    fallbackUnit = null,
}) {
    const allowedUnits = getAllowedCalendarThresholdUnits(itemType);
    const unit = allowedUnits.includes(threshold?.unit) ?
        threshold.unit :
        fallbackUnit ||
        DEFAULT_UNIT_BY_TYPE[itemType] ||
        CALENDAR_THRESHOLD_UNITS.DAYS;
    const value = Number.isInteger(Number(threshold?.value)) ?
        Number(threshold.value) :
        1;

    return {
        id: String(threshold?.id || getCalendarThresholdDefaultId(itemType)),
        unit,
        value,
        enabled: threshold?.enabled === true,
    };
}

/**
 * Builds the initial item threshold draft from item or admin data.
 * @param {object} params Draft params.
 * @param {string} params.itemType Calendar item type.
 * @param {object|null} [params.item=null] Calendar source item.
 * @param {object} [params.config={}] Runtime config.
 * @param {boolean} [params.isAllDay=false] Whether the item is all-day.
 * @returns {object} Item threshold draft.
 */
export function getCalendarItemThresholdDraft({
    itemType,
    item = null,
    config = {},
    isAllDay = false,
}) {
    if (Array.isArray(item?.notificationThresholds)) {
        return item.notificationThresholds
            .filter((entry) => entry?.enabled !== false)
            .map((entry) => getRenderableItemThresholdDraft(entry, {
                itemType,
                isAllDay,
            }));
    }

    const adminDefault = config?.calendarThresholdDefaults?.[itemType] || null;
    if (!isExistingItem(item) && adminDefault?.enabled === true) {
        return [getRenderableItemThresholdDraft({
            ...adminDefault,
            source: THRESHOLD_SOURCES.ADMIN_DEFAULT,
            sourceDefaultId: adminDefault.id || getCalendarThresholdDefaultId(itemType),
        }, { itemType, isAllDay })];
    }

    if (isExistingItem(item)) {
        return [];
    }

    return [];
}

/**
 * Renders a Calendar item threshold control.
 * @param {object} params Render params.
 * @param {string} params.itemType Calendar item type.
 * @param {object|null} params.threshold Threshold draft.
 * @param {string} params.label Display label.
 * @param {string} [params.description] Help text.
 * @param {string} [params.afterHeaderHtml] Trusted HTML rendered after the
 * section title.
 * @param {boolean} [params.isAllDay=false] Whether the item is all-day.
 * @param {boolean} [params.disabled=false] Whether controls are disabled.
 * @param {boolean} [params.readOnlyAdminDefaults=false] Whether admin default
 * rows should be read-only.
 * @returns {string} HTML string.
 */
export function renderCalendarItemThresholdControl({
    itemType,
    threshold,
    label,
    description = "",
    afterHeaderHtml = "",
    isAllDay = false,
    disabled = false,
    readOnlyAdminDefaults = false,
}) {
    const drafts = getRenderableItemThresholdDrafts(
        threshold,
        { itemType, isAllDay },
    );

    return `
        <div
            class="rounded-lg border border-gray-200 p-4"
            data-calendar-threshold-item="${attrValue(itemType)}"
            data-calendar-threshold-disabled="${disabled ? "true" : "false"}"
            data-calendar-threshold-empty="${drafts.length ? "false" : "true"}"
        >
            <div>
                <div class="min-w-0">
                    <div class="font-medium text-gray-900">${escapeHTML(label)}</div>
                    ${description ? `
                        <p class="mt-1 text-sm text-gray-500">${escapeHTML(description)}</p>
                    ` : ""}
                    ${afterHeaderHtml || ""}
                </div>
            </div>
            <div class="mt-3 space-y-2" data-calendar-threshold-list>
                ${drafts.map((draft) => renderCalendarThresholdRow({
        itemType,
        threshold: draft,
        isAllDay,
        disabled,
        readOnlyAdminDefaults,
    })).join("")}
            </div>
            <div class="mt-3">
                <button
                    type="button"
                    class="settings-add-row-btn disabled:cursor-not-allowed disabled:text-gray-400"
                    aria-label="Add notification"
                    data-calendar-threshold-add
                    ${disabled ? "disabled" : ""}
                >
                    + Add notification
                </button>
            </div>
        </div>
    `;
}

function renderCalendarThresholdRow({
    itemType,
    threshold,
    isAllDay = false,
    disabled = false,
    readOnlyAdminDefaults = false,
}) {
    const draft = getRenderableItemThresholdDraft(
        threshold,
        { itemType, isAllDay },
    );
    const allowedUnits = getAllowedCalendarThresholdUnits(itemType, { isAllDay });
    const unit = allowedUnits.includes(draft.unit) ? draft.unit : allowedUnits[0];
    const maxValue = getCalendarThresholdMaxValue(unit);
    const readOnly = readOnlyAdminDefaults &&
        draft.source === THRESHOLD_SOURCES.ADMIN_DEFAULT;
    const inputClasses = readOnly ?
        `${THRESHOLD_VALUE_INPUT_CLASSES} bg-gray-50 text-gray-500` :
        THRESHOLD_VALUE_INPUT_CLASSES;

    return `
        <div
            class="flex flex-wrap items-center gap-2 text-sm text-gray-500"
            data-calendar-threshold-row
            data-calendar-threshold-id="${attrValue(draft.id || createDraftThresholdId())}"
            data-calendar-threshold-source="${attrValue(draft.source)}"
            data-calendar-threshold-source-default-id="${attrValue(draft.sourceDefaultId || "")}"
            data-calendar-threshold-unit="${attrValue(unit)}"
            data-calendar-threshold-value="${attrValue(draft.value)}"
            data-calendar-threshold-readonly="${readOnly ? "true" : "false"}"
        >
            ${readOnly ? `
                <span
                    class="inline-flex min-h-10 items-center text-sm text-gray-600 sm:min-h-[30px]"
                    data-calendar-threshold-readonly-text
                >
                    ${escapeHTML(`${draft.value} ${getUnitDisplayLabel(unit, draft.value)} before`)}
                </span>
            ` : `
                <input
                    type="number"
                    min="1"
                    max="${attrValue(maxValue)}"
                    step="1"
                    inputmode="numeric"
                    aria-label="Reminder amount"
                    class="${inputClasses}"
                    value="${attrValue(draft.value)}"
                    data-calendar-threshold-field="value"
                    ${disabled ? "disabled" : ""}
                >
                <div class="shrink-0" data-calendar-threshold-unit-control></div>
                <span>before</span>
            `}
            ${readOnly ? "" : `<button
                type="button"
                class="settings-config-delete disabled:cursor-not-allowed disabled:opacity-50"
                title="Remove notification"
                aria-label="Remove notification"
                data-calendar-threshold-remove
                ${disabled ? "disabled" : ""}
            >
                <span class="material-symbols-outlined text-[18px] leading-none" aria-hidden="true">close</span>
            </button>`}
        </div>
    `;
}

/**
 * Binds item threshold control behavior.
 * @param {HTMLElement} root Root element.
 * @param {object} params Bind params.
 * @param {string} params.itemType Calendar item type.
 * @param {Function} [params.getIsAllDay] Returns all-day state.
 * @returns {void}
 */
export function bindCalendarItemThresholdControl(root, {
    itemType,
    getIsAllDay = () => false,
    readOnlyAdminDefaults = false,
}) {
    const row = root?.querySelector?.("[data-calendar-threshold-item]");
    if (!row) {return;}
    const disabled = row.dataset.calendarThresholdDisabled === "true";
    mountItemThresholdUnitControls(root, { itemType, isAllDay: getIsAllDay() });
    row.addEventListener("input", (event) => {
        if (event.target?.matches?.("[data-calendar-threshold-field]")) {
            markThresholdRowDirty(event.target);
        }
    });
    row.addEventListener("change", (event) => {
        if (event.target?.matches?.("[data-calendar-threshold-field]")) {
            markThresholdRowDirty(event.target);
            syncCalendarItemThresholdControl(root, {
                itemType,
                isAllDay: getIsAllDay(),
                readOnlyAdminDefaults,
            });
        }
    });
    row.addEventListener("click", (event) => {
        if (disabled) {return;}
        if (event.target?.closest?.("[data-calendar-threshold-add]")) {
            addCalendarThresholdRow(root, {
                itemType,
                isAllDay: getIsAllDay(),
                readOnlyAdminDefaults,
            });
            return;
        }
        const removeButton = event.target?.closest?.("[data-calendar-threshold-remove]");
        if (removeButton) {
            const thresholdRow = removeButton.closest("[data-calendar-threshold-row]");
            removeCalendarThresholdRow(thresholdRow);
            syncCalendarItemThresholdControl(root, {
                itemType,
                isAllDay: getIsAllDay(),
                readOnlyAdminDefaults,
            });
        }
    });
    syncCalendarItemThresholdControl(root, {
        itemType,
        isAllDay: getIsAllDay(),
        readOnlyAdminDefaults,
    });
}

/**
 * Synchronizes item threshold state after toggles or all-day changes.
 * @param {HTMLElement} root Root element.
 * @param {object} params Sync params.
 * @param {string} params.itemType Calendar item type.
 * @param {boolean} [params.isAllDay=false] Whether the item is all-day.
 * @returns {void}
 */
export function syncCalendarItemThresholdControl(root, {
    itemType,
    isAllDay = false,
    readOnlyAdminDefaults = false,
}) {
    const wrapper = root?.querySelector?.("[data-calendar-threshold-item]");
    if (!wrapper) {return;}
    const disabled = wrapper.dataset.calendarThresholdDisabled === "true";
    const allowedUnits = getAllowedCalendarThresholdUnits(itemType, { isAllDay });
    const rows = Array.from(wrapper.querySelectorAll("[data-calendar-threshold-row]"));
    rows.forEach((row) => {
        const readOnly = readOnlyAdminDefaults &&
            row.dataset.calendarThresholdSource === THRESHOLD_SOURCES.ADMIN_DEFAULT;
        const currentUnit = row.dataset.calendarThresholdUnit;
        const selectedUnit = allowedUnits.includes(currentUnit) ?
            currentUnit :
            allowedUnits[0];
        row.dataset.calendarThresholdReadonly = readOnly ? "true" : "false";
        row.dataset.calendarThresholdUnit = selectedUnit;
        const value = row.querySelector("[data-calendar-threshold-field=\"value\"]");
        if (value) {
            value.max = String(getCalendarThresholdMaxValue(selectedUnit));
            value.disabled = disabled || readOnly;
        }
        row.querySelector("[data-calendar-threshold-remove]")?.toggleAttribute(
            "disabled",
            disabled || readOnly,
        );
        mountOneItemThresholdUnitControl(row, { itemType, isAllDay });
    });
    syncThresholdEmptyState(wrapper);
}

/**
 * Reads item threshold controls into a payload list.
 * @param {HTMLElement} root Root element.
 * @param {object} params Read params.
 * @param {string} params.itemType Calendar item type.
 * @param {boolean} [params.isAllDay=false] Whether the item is all-day.
 * @returns {Array<object>} Threshold payload list.
 */
export function readCalendarItemThresholds(root, {
    itemType,
    isAllDay = false,
}) {
    const wrapper = root?.querySelector?.("[data-calendar-threshold-item]");
    if (!wrapper) {return [];}
    return Array.from(wrapper.querySelectorAll("[data-calendar-threshold-row]"))
        .map((row) => {
            const sourceDefaultId = row.dataset.calendarThresholdSourceDefaultId || null;
            const source = row.dataset.calendarThresholdDirty === "true" ?
                THRESHOLD_SOURCES.USER :
                row.dataset.calendarThresholdSource || THRESHOLD_SOURCES.USER;
            const normalized = normalizeCalendarThresholdDraft({
                id: row.dataset.calendarThresholdId || sourceDefaultId || "",
                unit: row.dataset.calendarThresholdUnit,
                value: row.querySelector("[data-calendar-threshold-field=\"value\"]")?.value ||
                    row.dataset.calendarThresholdValue,
                enabled: true,
            }, { itemType, isAllDay });

            if (!normalized) {return null;}
            return {
                ...normalized,
                source: source === THRESHOLD_SOURCES.ADMIN_DEFAULT ?
                    THRESHOLD_SOURCES.ADMIN_DEFAULT :
                    THRESHOLD_SOURCES.USER,
                sourceDefaultId,
            };
        })
        .filter(Boolean)
        .slice(0, MAX_THRESHOLDS);
}

function getRenderableItemThresholdDrafts(threshold, params) {
    if (Array.isArray(threshold)) {
        return threshold
            .filter((entry) => entry?.enabled !== false)
            .map((entry) => getRenderableItemThresholdDraft(entry, params));
    }
    if (threshold && typeof threshold === "object") {
        const draft = getRenderableItemThresholdDraft(threshold, params);
        return draft.enabled ? [draft] : [];
    }
    return [];
}

function markThresholdRowDirty(target) {
    const row = target?.closest?.("[data-calendar-threshold-row]");
    if (row) {
        row.dataset.calendarThresholdDirty = "true";
    }
}

function syncThresholdEmptyState(wrapper) {
    const hasRows = Boolean(
        wrapper.querySelector("[data-calendar-threshold-row]"),
    );
    wrapper.dataset.calendarThresholdEmpty = hasRows ? "false" : "true";
    const addButton = wrapper.querySelector("[data-calendar-threshold-add]");
    if (addButton) {
        addButton.disabled = wrapper.dataset.calendarThresholdDisabled === "true" ||
            wrapper.querySelectorAll("[data-calendar-threshold-row]").length >= MAX_THRESHOLDS;
    }
}

function addCalendarThresholdRow(root, {
    itemType,
    isAllDay = false,
    readOnlyAdminDefaults = false,
}) {
    const wrapper = root?.querySelector?.("[data-calendar-threshold-item]");
    const list = wrapper?.querySelector("[data-calendar-threshold-list]");
    if (!wrapper || !list) {return;}
    if (list.querySelectorAll("[data-calendar-threshold-row]").length >= MAX_THRESHOLDS) {
        return;
    }

    list.insertAdjacentHTML("beforeend", renderCalendarThresholdRow({
        itemType,
        threshold: {
            id: createDraftThresholdId(),
            unit: getAllowedCalendarThresholdUnits(itemType, { isAllDay })[0],
            value: 1,
            enabled: true,
            source: THRESHOLD_SOURCES.USER,
        },
        isAllDay,
        readOnlyAdminDefaults,
    }));
    const row = list.lastElementChild;
    if (row) {
        row.dataset.calendarThresholdDirty = "true";
        mountOneItemThresholdUnitControl(row, { itemType, isAllDay });
        row.querySelector("[data-calendar-threshold-field=\"value\"]")?.focus();
    }
    syncThresholdEmptyState(wrapper);
}

function removeCalendarThresholdRow(row) {
    if (!row) {return;}
    thresholdUnitControls.get(row)?.destroy();
    thresholdUnitControls.delete(row);
    row.remove();
}

function mountItemThresholdUnitControls(root, {
    itemType,
    isAllDay = false,
}) {
    root
        ?.querySelectorAll?.("[data-calendar-threshold-row]")
        .forEach((row) => mountOneItemThresholdUnitControl(row, {
            itemType,
            isAllDay,
        }));
}

function mountOneItemThresholdUnitControl(row, {
    itemType,
    isAllDay = false,
}) {
    const unitRoot = row.querySelector("[data-calendar-threshold-unit-control]");
    if (!unitRoot) {return;}
    thresholdUnitControls.get(row)?.destroy();
    thresholdUnitControls.delete(row);
    const allowedUnits = getAllowedCalendarThresholdUnits(itemType, { isAllDay });
    const value = allowedUnits.includes(row.dataset.calendarThresholdUnit) ?
        row.dataset.calendarThresholdUnit :
        allowedUnits[0];
    row.dataset.calendarThresholdUnit = value;
    if (allowedUnits.length <= 1) {
        setUnitRootLayout(unitRoot, false);
        unitRoot.replaceChildren(createPlainUnitText(value));
        return;
    }
    setUnitRootLayout(unitRoot, true);
    const control = createSelectControl({
        root: unitRoot,
        value,
        options: getUnitOptions(allowedUnits),
        accessibleName: "Reminder timing unit",
        placeholder: "Select timing",
        portalMenu: true,
        menuBoundary: "#form-modal",
        disabled: row.closest("[data-calendar-threshold-item]")
            ?.dataset.calendarThresholdDisabled === "true" ||
            row.dataset.calendarThresholdReadonly === "true",
        onChange: (nextValue) => {
            row.dataset.calendarThresholdUnit = String(nextValue || value);
            markThresholdRowDirty(row);
            const input = row.querySelector("[data-calendar-threshold-field=\"value\"]");
            if (input) {
                input.max = String(getCalendarThresholdMaxValue(
                    row.dataset.calendarThresholdUnit,
                ));
            }
        },
    });
    thresholdUnitControls.set(row, control);
}

function createPlainUnitText(unit) {
    const label = document.createElement("span");
    label.className = "inline-flex min-h-10 items-center text-base text-gray-500 sm:min-h-[30px] sm:text-sm";
    label.dataset.calendarThresholdUnitText = unit;
    label.textContent = getUnitLabel(unit);
    return label;
}

function setUnitRootLayout(root, usesSelect) {
    root.classList.toggle("w-[6.5rem]", usesSelect);
    root.classList.toggle("min-w-0", usesSelect);
}

function getRenderableItemThresholdDraft(threshold, {
    itemType,
    isAllDay = false,
}) {
    const allowedUnits = getAllowedCalendarThresholdUnits(itemType, { isAllDay });
    const fallbackUnit = DEFAULT_UNIT_BY_TYPE[itemType] ||
        CALENDAR_THRESHOLD_UNITS.DAYS;
    const unit = allowedUnits.includes(threshold?.unit) ?
        threshold.unit :
        fallbackUnit;
    const value = Number.isInteger(Number(threshold?.value)) ?
        Number(threshold.value) :
        1;
    return {
        id: String(threshold?.id || createDraftThresholdId()),
        unit,
        value,
        enabled: true,
        source: threshold?.source || THRESHOLD_SOURCES.USER,
        sourceDefaultId: threshold?.sourceDefaultId || null,
    };
}

/**
 * Renders a compact threshold default control.
 * @param {object} params Render params.
 * @param {string} params.itemType Calendar item type.
 * @param {object|null} params.threshold Threshold draft.
 * @param {string} params.label Display label.
 * @param {string} params.description Help text.
 * @param {string} [params.namePrefix="calendar-threshold"] Input name prefix.
 * @returns {string} HTML string.
 */
export function renderCalendarThresholdDefaultControl({
    itemType,
    threshold,
    label,
    description,
    namePrefix = "calendar-threshold",
}) {
    const draft = getCalendarThresholdDefaultDraft(threshold, { itemType });
    const maxValue = getCalendarThresholdMaxValue(draft.unit);
    const settingPrefix = `${namePrefix}-${itemType}`;

    return `
        <div class="rounded-lg border border-gray-200 p-4" data-calendar-threshold-default="${attrValue(itemType)}">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div class="min-w-0">
                    <div class="font-medium text-gray-900">${escapeHTML(label)}</div>
                    <p class="mt-1 text-sm text-gray-500">${escapeHTML(description)}</p>
                </div>
                <label class="toggle-switch shrink-0">
                    <input
                        type="checkbox"
                        data-calendar-threshold-field="enabled"
                        ${draft.enabled ? "checked" : ""}
                        aria-label="Enable ${escapeHTML(label)}"
                    >
                    <span class="slider"></span>
                </label>
            </div>
            <div class="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                <input
                    type="number"
                    min="1"
                    max="${attrValue(maxValue)}"
                    step="1"
                    inputmode="numeric"
                    name="${attrValue(settingPrefix)}-value"
                    aria-label="${attrValue(label)} amount"
                    class="${THRESHOLD_VALUE_INPUT_CLASSES}"
                    value="${attrValue(draft.value)}"
                    data-calendar-threshold-field="value"
                    ${draft.enabled ? "" : "disabled"}
                >
                <div
                    class="shrink-0"
                    data-calendar-threshold-default-unit-control
                    data-calendar-threshold-unit="${attrValue(draft.unit)}"
                    data-calendar-threshold-unit-name="${attrValue(settingPrefix)}-unit"
                ></div>
                <span>before</span>
                <button
                    type="button"
                    class="settings-config-delete disabled:cursor-not-allowed disabled:opacity-50"
                    title="Clear default"
                    aria-label="Clear default"
                    data-calendar-threshold-clear
                    ${draft.enabled ? "" : "disabled"}
                >
                    <span class="material-symbols-outlined text-[18px] leading-none" aria-hidden="true">close</span>
                </button>
            </div>
        </div>
    `;
}

/**
 * Binds custom Calendar threshold default unit controls.
 * @param {HTMLElement} root Root element.
 * @param {Function} onChange Callback fired when a default changes.
 * @returns {void}
 */
export function bindCalendarThresholdDefaultControls(root, onChange = () => {}) {
    root?.querySelectorAll?.("[data-calendar-threshold-default]").forEach((row) => {
        thresholdDefaultChangeHandlers.set(row, onChange);
        mountOneThresholdDefaultUnitControl(row, onChange);
    });
}

/**
 * Synchronizes a Calendar threshold default row.
 * @param {HTMLElement} row Threshold default row.
 * @param {object} params Sync params.
 * @param {string} params.itemType Calendar item type.
 * @param {boolean} params.enabled Whether the default is enabled.
 * @param {string} params.unit Selected unit.
 * @returns {void}
 */
export function syncCalendarThresholdDefaultControl(row, {
    itemType,
    enabled,
    unit,
}) {
    if (!row) {return;}
    const allowedUnits = getAllowedCalendarThresholdUnits(itemType);
    const selectedUnit = allowedUnits.includes(unit) ? unit : allowedUnits[0];
    const valueInput = row.querySelector(
        '[data-calendar-threshold-field="value"]',
    );
    if (valueInput) {
        valueInput.disabled = !enabled;
        valueInput.max = String(getCalendarThresholdMaxValue(selectedUnit));
    }
    row.querySelector("[data-calendar-threshold-clear]")?.toggleAttribute(
        "disabled",
        !enabled,
    );
    const unitRoot = row.querySelector("[data-calendar-threshold-default-unit-control]");
    if (unitRoot) {
        unitRoot.dataset.calendarThresholdUnit = selectedUnit;
    }
    mountOneThresholdDefaultUnitControl(
        row,
        thresholdDefaultChangeHandlers.get(row) || (() => {}),
    );
}

function mountOneThresholdDefaultUnitControl(row, onChange) {
    const itemType = row?.dataset?.calendarThresholdDefault;
    const root = row?.querySelector("[data-calendar-threshold-default-unit-control]");
    if (!itemType || !root) {return;}
    thresholdDefaultUnitControls.get(row)?.destroy();
    thresholdDefaultUnitControls.delete(row);
    const allowedUnits = getAllowedCalendarThresholdUnits(itemType);
    const value = allowedUnits.includes(root.dataset.calendarThresholdUnit) ?
        root.dataset.calendarThresholdUnit :
        allowedUnits[0];
    root.dataset.calendarThresholdUnit = value;
    const enabled = row.querySelector(
        '[data-calendar-threshold-field="enabled"]',
    )?.checked === true;
    if (allowedUnits.length <= 1) {
        setUnitRootLayout(root, false);
        root.replaceChildren(createPlainUnitText(value));
        return;
    }
    setUnitRootLayout(root, true);
    const control = createSelectControl({
        root,
        name: root.dataset.calendarThresholdUnitName || "",
        value,
        options: getUnitOptions(allowedUnits),
        accessibleName: "Default reminder unit",
        placeholder: "Select timing",
        portalMenu: true,
        menuBoundary: "#settings-content",
        disabled: !enabled,
        onChange: (nextValue) => {
            root.dataset.calendarThresholdUnit = String(nextValue || value);
            onChange(row);
        },
    });
    thresholdDefaultUnitControls.set(row, control);
}
